import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { AlertCircle } from 'lucide-react';
import { MoneyInput } from '../ui/money-input';
import { transactionService } from '../../services/transactionService';
import cashBoxService, { CashBox } from '../../services/cashBoxService';
import { CreateExpenseRequest } from '../../types';
import { showError } from '../Alerts';

interface IncomeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    preselectedCashBoxId?: number;
}

/**
 * Modal para crear ingresos de caja (manuales desde EXTERNO)
 * POST /api/transactions/income
 */
export default function IncomeModal({
    isOpen,
    onClose,
    onSuccess,
    preselectedCashBoxId,
}: IncomeModalProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);

    // Usamos el mismo request DTO que Expense (cashBoxId, amount, description)
    const [formData, setFormData] = useState<CreateExpenseRequest>({
        cashBoxId: 0,
        amount: 0,
        description: '',
    });

    // Cargar cajas disponibles
    useEffect(() => {
        const loadCashBoxes = async () => {
            if (isOpen) {
                try {
                    const boxes = await cashBoxService.getAllCashBoxes();
                    const active = boxes.filter((box) => box.isActive);
                    setCashBoxes(active);
                    if (preselectedCashBoxId) {
                        setFormData(prev => ({ ...prev, cashBoxId: preselectedCashBoxId }));
                    }
                } catch (error) {
                    console.error('Error al cargar cajas:', error);
                    setError('Error al cargar las cajas disponibles');
                }
            }
        };

        loadCashBoxes();
    }, [isOpen, preselectedCashBoxId]);

    // Resetear formulario al cerrar
    useEffect(() => {
        if (!isOpen) {
            setFormData({
                cashBoxId: 0,
                amount: 0,
                description: '',
            });
            setError(null);
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validaciones
        if (!formData.cashBoxId) {
            setError('Debe seleccionar una caja destino');
            return;
        }

        if (formData.amount <= 0) {
            setError('El monto debe ser mayor a cero');
            return;
        }

        try {
            setLoading(true);
            await transactionService.createIncome(formData);
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Error al crear ingreso:', error);
            const msg = error.response?.data?.message || 'Error al registrar el ingreso. Por favor, intente nuevamente.';
            setError(msg);
            showError(msg);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number, currency: string) => {
        return `${currency} ${amount.toLocaleString('es-AR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };



    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle className="text-emerald-700 dark:text-emerald-500">Registrar Ingreso a Caja</DialogTitle>
                        <DialogDescription>
                            Registrar una entrada manual de dinero a una caja desde el exterior del sistema.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        {error && (
                            <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-100 rounded-md">
                                <AlertCircle className="w-4 h-4" />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="grid gap-2">
                            <Label htmlFor="cashBox">Caja Destino *</Label>
                            <Select
                                value={formData.cashBoxId.toString()}
                                onValueChange={(value) =>
                                    setFormData({ ...formData, cashBoxId: parseInt(value) })
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar caja" />
                                </SelectTrigger>
                                <SelectContent>
                                    {cashBoxes.map((box) => (
                                        <SelectItem key={box.id} value={box.id.toString()}>
                                            {box.name} - {formatCurrency(box.balance, box.currency)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="amount">Monto de Ingreso *</Label>
                            <MoneyInput
                                value={formData.amount === 0 ? '' : formData.amount}
                                onValueChange={(value) =>
                                    setFormData({ ...formData, amount: value ?? 0 })
                                }
                                currency={cashBoxes.find((b) => b.id === formData.cashBoxId)?.currency || 'ARS'}
                                placeholder="0,00"
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="description">Detalle o Motivo</Label>
                            <Textarea
                                id="description"
                                placeholder="Ej: Aporte de capital, ingresos varios, etc."
                                rows={3}
                                value={formData.description}
                                onChange={(e) =>
                                    setFormData({ ...formData, description: e.target.value })
                                }
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            {loading ? 'Registrando...' : 'Confirmar Ingreso'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
